import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { RefreshTokenRequest } from '@lib/interfaces/auth.interface';
import { AuthService } from '@lib/services';
import { clearAppSession, selectAppSession, setAppSession } from '@lib/store';
import { Store } from '@ngrx/store';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';

/**
 * Interceptor that adds an Authorization header to requests that are authenticated and target the API URL.
 * Handles 401 errors by calling the refresh token endpoint and updating the session in the store.
 */
let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

function addAuthorizationHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
        setHeaders: {
            Authorization: `Bearer ${token}`,
        },
    });
}

function handleRefreshTokenSuccess(
    response: any,
    appSession: any,
    req: HttpRequest<any>,
    store: Store,
    authService: AuthService,
    next: any,
): Observable<any> {
    isRefreshing = false;
    refreshTokenSubject.next(response.data.accessToken);
    store.dispatch(setAppSession({ session: { ...appSession, ...response.data } }));
    authService.initializeSession(response.data);
    const retriedReq = addAuthorizationHeader(req, response.data.accessToken);
    return next(retriedReq);
}

function handleRefreshTokenError(
    refreshError: any,
    store: Store,
    router: Router,
    authService: AuthService,
): Observable<any> {
    isRefreshing = false;
    refreshTokenSubject.next(null);

    if (refreshError.status === 401) {
        authService.logout();
        router.navigate(['auth/login']);
        return throwError(() => refreshError);
    }

    store.dispatch(clearAppSession());
    router.navigate(['auth/login']);
    return throwError(() => refreshError);
}

function performTokenRefresh(
    appSession: any,
    req: HttpRequest<any>,
    authService: AuthService,
    store: Store,
    router: Router,
    next: any,
): Observable<any> {
    isRefreshing = true;
    refreshTokenSubject.next(null);
    const refreshRequest: RefreshTokenRequest = {
        Authorization: `Bearer ${appSession.accessToken}`,
        refreshToken: appSession.refreshToken,
    };
    return authService.refreshToken(refreshRequest).pipe(
        switchMap((response) => {
            return handleRefreshTokenSuccess(response, appSession, req, store, authService, next);
        }),
        catchError((refreshError) => {
            return handleRefreshTokenError(refreshError, store, router, authService);
        }),
    );
}

function waitForTokenRefresh(
    req: HttpRequest<any>,
    authService: AuthService,
    appSession: any,
    next: any,
): Observable<any> {
    authService.initializeSession(appSession);
    return refreshTokenSubject.pipe(
        filter((token): token is string => token != null),
        take(1),
        switchMap((token) => {
            const retriedReq = addAuthorizationHeader(req, token);
            return next(retriedReq);
        }),
    );
}

function handleUnauthorizedError(
    error: any,
    appSession: any,
    req: HttpRequest<any>,
    authService: AuthService,
    store: Store,
    router: Router,
    next: any,
): Observable<any> {
    if (error.status === 401 && req.url.includes('/auth/refresh-token')) {
        return handleRefreshTokenError(error, store, router, authService);
    }

    if (error.status !== 401 || !appSession?.refreshToken) {
        return throwError(() => error);
    }

    if (isRefreshing) {
        return waitForTokenRefresh(req, authService, appSession, next);
    }

    return performTokenRefresh(appSession, req, authService, store, router, next);
}

export const jwtInterceptor: HttpInterceptorFn = (request, next) => {
    const authService = inject(AuthService);
    const store = inject(Store);
    const router = inject(Router);

    const isRequestAuthorized = authService.isAuthenticated && request.url.startsWith(environment.API_URL);

    return store.select(selectAppSession).pipe(
        take(1),
        switchMap((appSession) => {
            let req = request;
            if (isRequestAuthorized && appSession?.accessToken) {
                req = addAuthorizationHeader(request, appSession.accessToken);

                if (authService.currentUserInitials$.value === 'U') {
                    authService.currentUserInitials$.next(appSession.initials);
                }
            }
            return next(req).pipe(
                catchError((error) => {
                    return handleUnauthorizedError(error, appSession, req, authService, store, router, next);
                }),
            );
        }),
    );
};
